import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Offer section used for Events (#events) and Branding (#branding).
 * Top: 50/50 intro (text left, image collage right on desktop;
 * stacked text -> collage -> cards on mobile). Below: three offer cards.
 *
 * Props:
 *   - sectionId: anchor id for the <section>
 *   - testIdPrefix: "events" | "branding" (drives data-testid naming)
 *   - blockKey: key under translations root — "events" | "branding"
 *   - images: array of image sources for the collage (3 = 1 large + 2 small,
 *             2 = asymmetric side-by-side)
 */
export const OffersSection = ({ sectionId, testIdPrefix, blockKey, images = [] }) => {
  const { t } = useLanguage();
  const data = t[blockKey];
  if (!data) return null;

  const alts = data.collageAlts || [];

  return (
    <section
      id={sectionId}
      data-testid={`${testIdPrefix}-section`}
      className="py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        {/* 50/50 intro: text left, collage right (stacked on mobile) */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0055FF]">
              {data.kicker}
            </div>
            <h2 className="mt-3 font-heading text-3xl md:text-5xl font-bold tracking-tight text-neutral-900">
              {data.title}
            </h2>
            {data.subtitle && (
              <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
                {data.subtitle}
              </p>
            )}
            {data.spaceLine && (
              <p
                data-testid={`${testIdPrefix}-space-note`}
                className="mt-3 text-sm md:text-base font-semibold text-neutral-800"
              >
                {data.spaceLine}
              </p>
            )}
          </div>

          {images.length === 3 && (
            <div
              data-testid={`${testIdPrefix}-collage`}
              className="grid grid-cols-2 gap-3 md:gap-4"
            >
              <div className="col-span-2 relative overflow-hidden rounded-3xl aspect-[16/9]">
                <img
                  src={images[0]}
                  alt={alts[0] || ""}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="relative overflow-hidden rounded-3xl aspect-[4/3]"
                >
                  <img
                    src={images[i]}
                    alt={alts[i] || ""}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}

          {images.length === 2 && (
            <div
              data-testid={`${testIdPrefix}-collage`}
              className="grid grid-cols-5 gap-3 md:gap-4 items-start"
            >
              <div className="col-span-3 relative overflow-hidden rounded-3xl aspect-[4/3]">
                <img
                  src={images[0]}
                  alt={alts[0] || ""}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <div className="col-span-2 relative overflow-hidden rounded-3xl aspect-[3/4] mt-8">
                <img
                  src={images[1]}
                  alt={alts[1] || ""}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 md:mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {data.cards.map((card, i) => (
            <motion.article
              key={card.label}
              data-testid={`${testIdPrefix}-card-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="relative flex flex-col rounded-3xl bg-[#F8FAFC] border border-slate-200/70 p-7 md:p-8"
            >
              <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#0055FF]">
                {card.label}
              </div>
              <h3 className="mt-3 font-heading text-2xl font-bold text-neutral-900 leading-tight">
                {card.title}
              </h3>
              {card.subtitle && (
                <p className="mt-2 text-neutral-600 text-base">{card.subtitle}</p>
              )}
              <ul className="mt-6 space-y-3">
                {card.items.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-neutral-700 text-sm leading-relaxed">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#0055FF]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              {card.note && (
                <p className="mt-5 text-xs text-neutral-500 italic leading-relaxed">
                  {card.note}
                </p>
              )}
            </motion.article>
          ))}
        </div>

        {data.ctaLabel && (
          <div className="mt-10 md:mt-12">
            <a
              href={data.ctaHref || "#pricing"}
              data-testid={`${testIdPrefix}-cta`}
              className="group inline-flex items-center gap-2 rounded-full bg-[#0055FF] text-white px-6 py-3.5 text-sm md:text-base font-semibold hover:bg-[#0044dd] transition-all shadow-xl shadow-[#0055FF]/20"
            >
              {data.ctaLabel}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

const EVENTS_IMAGES = [
  "/images/fp-events-1.webp",
  "/images/fp-events-2.webp",
  "/images/fp-events-3.webp",
];

const BRANDING_IMAGES = [
  "/images/fp-branding-1.webp",
  "/images/fp-branding-2.jpg",
];

export const Events = () => (
  <OffersSection
    sectionId="events"
    testIdPrefix="events"
    blockKey="events"
    images={EVENTS_IMAGES}
  />
);

export const Branding = () => (
  <OffersSection
    sectionId="branding"
    testIdPrefix="branding"
    blockKey="branding"
    images={BRANDING_IMAGES}
  />
);

export default OffersSection;
